// StandorteTab.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Input,
  InputAdornment,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Pagination,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { ReactComponent as EditSvg } from "../../../assets/EditSvg.svg";
import { useNavigate } from "react-router";
import { withCurrentAccountPrefix } from "../../../utils/accountMode";

const UI = {
  border: "#E6E8EB",
  statusActive: "#1976d2",
  statusInactive: "#9E9E9E",
};

// --- Dummy locations (feel free to replace later with Stepper data)
const LOCATIONS = [
  { id: 1, standortname: "Werk Hamburg", unternehmen: "Delta Hamburg GmbH", ort: "Hamburg", land: "Deutschland", maschinen: 23, active: true },
  { id: 2, standortname: "Werk Berlin", unternehmen: "Delta Berlin GmbH", ort: "Berlin", land: "Deutschland", maschinen: 23, active: true },
  { id: 3, standortname: "Werk Pforzheim", unternehmen: "Delta Engineering GmbH", ort: "Pforzheim", land: "Deutschland", maschinen: 23, active: false },
  { id: 4, standortname: "Werk Kulmbach I", unternehmen: "Delta Electronics GmbH", ort: "Kulmbach", land: "Deutschland", maschinen: 23, active: true },
  { id: 5, standortname: "Werk Kulmbach II", unternehmen: "Delta Labs GmbH", ort: "Kulmbach", land: "Deutschland", maschinen: 23, active: true },
  { id: 6, standortname: "X-Ray Delta Labs", unternehmen: "Delta Spain Ltd.", ort: "Palma", land: "Spanien", maschinen: 23, active: true },
  { id: 7, standortname: "Werk Győr", unternehmen: "Delta Hungary Kft.", ort: "Győr", land: "Ungarn", maschinen: 15, active: true },
  { id: 8, standortname: "Werk Wien", unternehmen: "Delta Austria GmbH", ort: "Wien", land: "Österreich", maschinen: 12, active: false },
  { id: 9, standortname: "Werk Zürich", unternehmen: "Delta Schweiz AG", ort: "Zürich", land: "Schweiz", maschinen: 17, active: true },
  { id: 10, standortname: "Werk Prag", unternehmen: "Delta CZ s.r.o.", ort: "Prag", land: "Tschechien", maschinen: 19, active: true },
  { id: 11, standortname: "Werk Lyon", unternehmen: "Delta France SAS", ort: "Lyon", land: "Frankreich", maschinen: 21, active: true },
  { id: 12, standortname: "Werk Mailand", unternehmen: "Delta Italia S.p.A.", ort: "Mailand", land: "Italien", maschinen: 16, active: false },
];

const norm = (v) =>
  String(v)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export default function LocationsTab() {
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState("standortname"); // default sort
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();

  // filter
  const filtered = useMemo(() => {
    const tokens = norm(search).split(/\s+/).filter(Boolean);
    return LOCATIONS.filter((r) => {
      const searchable = [
        r.standortname,
        r.unternehmen,
        r.ort,
        r.land,
        r.machines, // not used but fine
      ]
        .map(norm)
        .join(" ");
      return tokens.length === 0 || tokens.every((t) => searchable.includes(t));
    });
  }, [search]);

  // sort
  const sorted = useMemo(() => {
    const data = [...filtered];
    data.sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (typeof av === "string") av = av.toLocaleLowerCase();
      if (typeof bv === "string") bv = bv.toLocaleLowerCase();
      if (av < bv) return order === "asc" ? -1 : 1;
      if (av > bv) return order === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [filtered, orderBy, order]);

  // pagination
  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(col);
      setOrder("asc");
    }
  };

  const handleEdit = () => {
    navigate(withCurrentAccountPrefix("/edit-kunden-locations"))
  }

  return (
    <div className="kundentable_wrapper bxShadow">
      {/* Header line: title + search */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 320px",
          alignItems: "center",
          gap: 2,
          px: 2.5,
          py: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "800 !important", color: "var(--color-primary)", letterSpacing: 0.2 }}
        >
          Standorte ({filtered.length})  <span style={{ marginLeft:"15px",marginTop:"10px"}} className="cp" onClick={handleEdit}>{<EditSvg/>}</span>
        </Typography>

        <Input
          fullWidth
          placeholder="Standorte durchsuchen"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton tabIndex={-1}>
                <Search />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>

      {/* Table */}
      <Table
        stickyHeader
        aria-label="Standorte"
        sx={{
          "& th, & td": {
            borderBottom: `1px solid ${UI.border}`,
            px: 2.5,
          },
          "& th": {
            bgcolor: "#FFFFFF",
            color: "var(--color-primary)",
            fontFamily: "Roboto",
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: 0.15,
            py: 2,
          },
          "& td": {
            color: "#38748D",
            fontFamily: "Roboto",
            fontSize: 16,
            letterSpacing: 0.5,
            py: 2,
          },
          paddingLeft: "24px",
          paddingRight: "24px"
        }}
      >
        <TableHead>
          <TableRow>
            {/* status dot like in Kunden table */}
            <TableCell sx={{ width: 40, pl: 2 }}>
              {/* empty header cell */}
            </TableCell>

            <TableCell sortDirection={orderBy === "standortname" ? order : false}>
              <TableSortLabel
                active={orderBy === "standortname"}
                direction={orderBy === "standortname" ? order : "asc"}
                onClick={() => handleSort("standortname")}
              >
                Standortname
              </TableSortLabel>
            </TableCell>

            <TableCell sortDirection={orderBy === "unternehmen" ? order : false}>
              <TableSortLabel
                active={orderBy === "unternehmen"}
                direction={orderBy === "unternehmen" ? order : "asc"}
                onClick={() => handleSort("unternehmen")}
              >
                Unternehmen
              </TableSortLabel>
            </TableCell>

            <TableCell sortDirection={orderBy === "ort" ? order : false}>
              <TableSortLabel
                active={orderBy === "ort"}
                direction={orderBy === "ort" ? order : "asc"}
                onClick={() => handleSort("ort")}
              >
                Ort
              </TableSortLabel>
            </TableCell>

            <TableCell sortDirection={orderBy === "land" ? order : false}>
              <TableSortLabel
                active={orderBy === "land"}
                direction={orderBy === "land" ? order : "asc"}
                onClick={() => handleSort("land")}
              >
                Land
              </TableSortLabel>
            </TableCell>

            <TableCell align="right" sortDirection={orderBy === "maschinen" ? order : false}>
              <TableSortLabel
                active={orderBy === "maschinen"}
                direction={orderBy === "maschinen" ? order : "asc"}
                onClick={() => handleSort("maschinen")}
              >
                Maschinen
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {paginated.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell sx={{ width: 40, pl: 2 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: row.active ? UI.statusActive : UI.statusInactive,
                  }}
                />
              </TableCell>

              <TableCell>{row.standortname}</TableCell>
              <TableCell>{row.unternehmen}</TableCell>
              <TableCell>{row.ort}</TableCell>
              <TableCell>{row.land}</TableCell>
              <TableCell align="right">{row.maschinen}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pager */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 3,
          color: "var(--color-primary)",
        }}
      >
        <Pagination
          count={Math.max(1, Math.ceil(filtered.length / rowsPerPage))}
          page={page + 1}
          onChange={(_, value) => setPage(value - 1)}
          shape="rounded"
        />

        <FormControl variant="standard" sx={{ minWidth: 160 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ color: "rgba(0,0,0,0.5)" }}
          >
            {[10, 25, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size} Einträge pro Seite
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </div>
  );
}
